import { Header, Content, InlineHeader, InlineSection } from '@/components/DashboardComponents'
import { useLoaderData } from 'react-router'
import { List, ListItem, ItemColumn } from '@/components/List'
import { shortDate } from '@/shared/utils'
import { ActivityGraph } from '@/pages/internal/ActivityGraph'
import { GenericEmptyListPlaceholder } from '@/DashboardLayout'
import { FormProvider } from '@/form/FormProvider'
import { Textbox, Dropdown, DropdownOption, Spacing, HalfSpacing } from '@/form/FormComponents'

export function InternalUser() {
  const user = useLoaderData().data.value;

  return (
    <>
      <Header
        title={user.name || user.email || "User"}
        backButton={true}
      />
      <Content>
        <div className="internal_user_container">
          <div>
            <InlineHeader title="User details" />
            <InlineSection>
              <FormProvider initial_data={{ name: user.name || "", email: user.email || "", role: user.role || null }} readOnly={true}>
                <Textbox name="name" title="Name" />
                <Spacing/>
                <Textbox name="email" title="Email" />
                <Spacing/>
                <Dropdown name="role" title="Role">
                  <DropdownOption name="student" text="Student" />
                  <DropdownOption name="educator" text="Educator" />
                </Dropdown>
                <HalfSpacing/>
              </FormProvider>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <div className="text_section">
                  <div>Organization</div>
                  <div className="text">{user.organization_id ? user._organization_name : "—"}</div>
                </div>
                <div className="text_section">
                  <div>Date created</div>
                  <div className="text">{user.date_created ? shortDate(user.date_created) : "—"}</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                <div className="text_section">
                  <div>Email verified</div>
                  <div className="text">{user.emailVerified ? "Yes" : "—"}</div>
                </div>
                <div className="text_section">
                  <div>Terms</div>
                  <div className="text">{user.terms_accepted || "—"}</div>
                </div>
                <div className="text_section">
                  <div>Approved</div>
                  <div className="text">{user._is_approved ? "Yes" : "—"}</div>
                </div>
              </div>
            </InlineSection>
          </div>
          <div>
            <InlineHeader title="Activity Graph" />
            <InlineSection>
              <div style={{ height: '300px' }}>
                <ActivityGraph uid={user._id} />
              </div>
            </InlineSection>
          </div>
          <div>
            <div>
              <InlineHeader title="Courses" />
              <InlineSection halfMarginTop={true}>
                {user._course_memberships.length > 0 ? (
                  <List columns={[{ name: "Course" }, { name: "Role", width: 80 }]} hide_header={true} menu={false} compact={true}>
                    {user._course_memberships.map(cm => (
                      <ListItem key={cm.course_id} icon="book-open-variant-outline" onClick={() => { window.open("/courses/" + cm.course_id, "_blank") }}>
                        <ItemColumn>{cm._course_name || cm.course_id}{cm._course_archived ? " (archived)" : ""}</ItemColumn>
                        <ItemColumn>{cm.role}</ItemColumn>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <GenericEmptyListPlaceholder text="No courses" />
                )}
              </InlineSection>
            </div>
            <div>
              <InlineHeader title="Recent scenarios" />
              <InlineSection halfMarginTop={true}>
                {user._recent_scenarios && user._recent_scenarios.length > 0 ? (
                  <List columns={[{ name: "Scenario" }, { name: "Created", width: 90 }]} hide_header={true} menu={false} compact={true}>
                    {user._recent_scenarios.map(s => (
                      <ListItem key={s._id} icon="forum-outline" iconColor="var(--scenario_file_color)" onClick={() => { window.open("/scenarios/" + s._id, "_blank") }}>
                        <ItemColumn>{s.data?.title || s._id}</ItemColumn>
                        <ItemColumn>{s.date_created ? shortDate(s.date_created) : "—"}</ItemColumn>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <GenericEmptyListPlaceholder text="No scenarios" />
                )}
              </InlineSection>
            </div>
            <div>
              <InlineHeader title="Recent rubrics" />
              <InlineSection halfMarginTop={true}>
                {user._recent_rubrics && user._recent_rubrics.length > 0 ? (
                  <List columns={[{ name: "Rubric" }, { name: "Created", width: 90 }]} hide_header={true} menu={false} compact={true}>
                    {user._recent_rubrics.map(r => (
                      <ListItem key={r._id} icon="bullseye-arrow" iconColor="var(--rubric_file_color)" onClick={() => { window.open("/rubrics/" + r._id, "_blank") }}>
                        <ItemColumn>{r.data?.title || r._id}</ItemColumn>
                        <ItemColumn>{r.date_created ? shortDate(r.date_created) : "—"}</ItemColumn>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <GenericEmptyListPlaceholder text="No rubrics" />
                )}
              </InlineSection>
            </div>
            <div>
              <InlineHeader title="Departments" />
              <InlineSection halfMarginTop={true}>
                {user._organization_memberships.length > 0 ? (
                  <List columns={[{ name: "Department" }, { name: "Roles", width: 120 }]} hide_header={true} menu={false} compact={true}>
                    {user._organization_memberships.map(om => (
                      <ListItem key={om.organization_id} icon="domain" onClick={() => { window.open("/organizations/" + om.organization_id, "_blank") }}>
                        <ItemColumn>{om._organization_name || om.organization_id}</ItemColumn>
                        <ItemColumn>{(om.roles || []).join(", ") || "—"}{!om.approved && " (pending)"}</ItemColumn>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <GenericEmptyListPlaceholder text="No departments" />
                )}
              </InlineSection>
            </div>
          </div>
        </div>
      </Content>
    </>
  )
}
